/**
 * True offset curves for laser "searching" outlines.
 *
 * SVG has no offset primitive, and the old trick (a wide black stroke with a
 * white stroke masking its inside) paints the gap between panel and searching
 * line WHITE — a fill the factory must not have. Here the silhouette is
 * flattened to polygons and offset with Clipper (round joins), which yields a
 * stroke-only path with nothing inside.
 */
import ClipperLib from 'clipper-lib';
import { flattenPathData, IDENTITY, type Mat } from './clipFlatten';

/** Clipper works on integers; a thousandth of a unit is far below cut precision. */
const SCALE = 1000;

interface IntPoint {
  X: number;
  Y: number;
}

function toClipperPaths(polylines: Array<{ points: Array<[number, number]> }>): IntPoint[][] {
  return polylines
    .filter((p) => p.points.length >= 3)
    .map((p) => p.points.map(([x, y]) => ({ X: Math.round(x * SCALE), Y: Math.round(y * SCALE) })));
}

function toPathData(paths: IntPoint[][]): string {
  return paths
    .filter((p) => p.length >= 3)
    .map(
      (p) =>
        p
          .map(
            (pt, i) => `${i ? 'L' : 'M'}${(pt.X / SCALE).toFixed(3)} ${(pt.Y / SCALE).toFixed(3)}`
          )
          .join('') + 'Z'
    )
    .join('');
}

/**
 * Intersection of two or more closed outlines, each given as path data plus
 * the matrix that places it in a common space. Curves are flattened to
 * within `tolerance` first. The empty string when the outlines are valid but
 * don't overlap (a shape clipped away entirely); null when a spec has no
 * closed geometry or the geometry engine is unavailable.
 */
export async function intersectPathData(
  specs: Array<{ d: string; matrix: Mat }>,
  tolerance: number
): Promise<string | null> {
  if (specs.length < 2 || specs.some((s) => !s.d)) return null;
  const polygons: IntPoint[][][] = [];
  for (const spec of specs) {
    const polylines = await flattenPathData(spec.d, spec.matrix, tolerance);
    const paths = polylines ? toClipperPaths(polylines) : [];
    if (!paths.length) return null;
    polygons.push(paths);
  }
  try {
    let acc = polygons[0];
    for (let i = 1; i < polygons.length; i++) {
      const clipper = new ClipperLib.Clipper();
      clipper.AddPaths(acc, ClipperLib.PolyType.ptSubject, true);
      clipper.AddPaths(polygons[i], ClipperLib.PolyType.ptClip, true);
      const out: IntPoint[][] = [];
      clipper.Execute(
        ClipperLib.ClipType.ctIntersection,
        out,
        ClipperLib.PolyFillType.pftNonZero,
        ClipperLib.PolyFillType.pftNonZero
      );
      if (!out.length) return '';
      acc = out;
    }
    return toPathData(acc);
  } catch {
    return null;
  }
}

/**
 * Offset closed path data outward by `delta` (same units as `d`). Curves are
 * flattened to within `tolerance` first; holes and multiple outlines are
 * handled by a union pass that normalises orientation. Null when the path
 * has no closed geometry or the geometry engine is unavailable.
 */
export async function offsetPathData(
  d: string,
  delta: number,
  tolerance: number
): Promise<string | null> {
  if (!d || !(delta > 0)) return null;
  const polylines = await flattenPathData(d, IDENTITY, tolerance);
  if (!polylines?.length) return null;
  const subject = toClipperPaths(polylines);
  if (!subject.length) return null;

  try {
    // Union first: consistent winding for outers/holes, no self-intersections.
    const clipper = new ClipperLib.Clipper();
    clipper.AddPaths(subject, ClipperLib.PolyType.ptSubject, true);
    const unioned: IntPoint[][] = [];
    clipper.Execute(
      ClipperLib.ClipType.ctUnion,
      unioned,
      ClipperLib.PolyFillType.pftNonZero,
      ClipperLib.PolyFillType.pftNonZero
    );
    if (!unioned.length) return null;

    const offsetter = new ClipperLib.ClipperOffset(2, tolerance * SCALE);
    offsetter.AddPaths(unioned, ClipperLib.JoinType.jtRound, ClipperLib.EndType.etClosedPolygon);
    const solution: IntPoint[][] = [];
    offsetter.Execute(solution, delta * SCALE);
    const out = toPathData(solution);
    return out || null;
  } catch {
    return null;
  }
}
